import { translate, useLocale } from "../localization";
import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  ButtonGroup,
  IconButton,
  Tooltip,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useNavigate } from "react-router-dom";
import { useContent } from "../context/ContentContext";
import { AppButton, Panel } from "../components/ui";
import "./CollectionsPage.scss";

const getCollectionName = (collection) =>
  collection?.name || collection?.collectionName || collection?.title || translate("Untitled Collection");

const getCollectionDescription = (collection) =>
  collection?.description || collection?.summary || translate("No Description");

const getCollectionDate = (collection) =>
  collection?.dateModified ||
  collection?.updatedAt ||
  collection?.modifiedAt ||
  collection?.status?.timestamp ||
  collection?.dateCreated ||
  collection?.createdAt ||
  "";

const CollectionsPage = () => {
  const locale = useLocale();
  const navigate = useNavigate();
  const {
    myCollections,
    fetchCollections,
    collectionsLoading,
  } = useContent();
  const [collections, setCollections] = useState([]);

  // States for sorting
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // If collections are missing in context, fetch them.
  useEffect(() => {
    if ((!myCollections || myCollections.length === 0) && !collectionsLoading) {
      fetchCollections();
    }
  }, [myCollections, collectionsLoading, fetchCollections]);

  // Update local state when context collections change.
  useEffect(() => {
    if (myCollections) {
      setCollections(myCollections);
    }
  }, [myCollections]);

  // ----- Sorting -----
  const sortedCollections = useMemo(() => {
    return [...collections].sort((a, b) => {
      if (sortField === "name") {
        const aValue = getCollectionName(a).toLowerCase();
        const bValue = getCollectionName(b).toLowerCase();
        return sortOrder === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (sortField === "dateModified") {
        const aTime = getCollectionDate(a) ? new Date(getCollectionDate(a)).getTime() : 0;
        const bTime = getCollectionDate(b) ? new Date(getCollectionDate(b)).getTime() : 0;
        return sortOrder === "asc" ? aTime - bTime : bTime - aTime;
      }

      return 0;
    });
  }, [collections, sortField, sortOrder, locale]);

  if (collectionsLoading) {
    return (
      <div className="collectionsPage">
        <div className="collectionsLoading">{translate("Loading...")}</div>
      </div>
    );
  }

  return (
    <div className="collectionsPage">
      <Typography className="collectionsTitle" variant="h4">{translate("Collections")}</Typography>

      {/* Sorting Controls */}
      <div className="collectionsSortRow">
        <div className="collectionsSortGroup">
          <ButtonGroup size="small" aria-label={translate("sort collections")}>
            <AppButton
              kind={sortField === "name" ? "primary" : "secondary"}
              onClick={() => setSortField("name")}
            >{translate("Sort: Name")}</AppButton>
            <AppButton
              kind={sortField === "dateModified" ? "primary" : "secondary"}
              onClick={() => setSortField("dateModified")}
            >{translate("Sort: Date Updated")}</AppButton>
          </ButtonGroup>
        </div>

        <Tooltip title={sortOrder === "asc" ? translate("Ascending") : translate("Descending")}>
          <IconButton
            className="collectionsSortArrow"
            size="small"
            onClick={() => setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))}
          >
            {sortOrder === "asc" ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
          </IconButton>
        </Tooltip>
      </div>

      {/* Collections List */}
      <div className="collectionsList app-row-list">
        {sortedCollections.map((collection) => (
          <Panel key={collection.id} className="collectionCard" hoverable>
            <div className="collectionCardContent">
              <div className="collectionCardInfo">
                <div className="collectionCardName">{getCollectionName(collection)}</div>
                <div className="collectionCardId">{collection?.id || translate("No ID")}</div>
                {collection?.status?.type && (
                  <div className="collectionCardStatus">{collection.status.type}</div>
                )}
              </div>
              <div className="collectionCardDesc">
                {getCollectionDescription(collection)}
              </div>
              <div className="collectionCardActions">
                <AppButton
                  className="collectionCardBtn"
                  kind="secondary"
                  onClick={() => navigate(`/collections/${collection.id}`)}
                >{translate("Open")}</AppButton>
              </div>
            </div>
          </Panel>
        ))}
      </div>
    </div>
  );
};

export default CollectionsPage;
